import {
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  type User,
} from 'firebase/auth'
import { FirebaseError } from 'firebase/app'
import { doc, getDoc } from 'firebase/firestore'

import { auth, db } from '@/lib/firebase'
import { AppError } from '@/lib/errors'

export const ADMINS_COLLECTION = 'admins'

export type { User }

export function watchAuthState(listener: (user: User | null) => void): () => void {
  return onAuthStateChanged(auth, listener)
}

export function currentUser(): User | null {
  return auth.currentUser
}

export async function isActiveAdmin(uid: string): Promise<boolean> {
  const snapshot = await getDoc(doc(db, ADMINS_COLLECTION, uid))
  if (!snapshot.exists()) return false
  return snapshot.data()?.active === true
}

export async function signIn(email: string, password: string): Promise<void> {
  let user: User
  try {
    const credential = await signInWithEmailAndPassword(auth, email.trim(), password)
    if (!credential.user) throw new AppError('Не удалось войти', 'auth')
    user = credential.user
  } catch (error) {
    if (error instanceof FirebaseError) throw new AppError(mapAuthError(error), 'auth')
    throw error
  }

  const allowed = await isActiveAdmin(user.uid)
  if (!allowed) {
    await firebaseSignOut(auth)
    throw new AppError(
      'У этого аккаунта нет доступа к админ-панели.\n' +
        'Добавьте UID в коллекцию admins в Firestore.',
      'auth',
    )
  }
}

export function signOut(): Promise<void> {
  return firebaseSignOut(auth)
}

export async function sendPasswordReset(email: string): Promise<void> {
  try {
    await sendPasswordResetEmail(auth, email.trim())
  } catch (error) {
    if (error instanceof FirebaseError) throw new AppError(mapAuthError(error), 'auth')
    throw error
  }
}

function mapAuthError(error: FirebaseError): string {
  switch (error.code) {
    case 'auth/invalid-email':
      return 'Некорректный email'
    case 'auth/user-disabled':
      return 'Аккаунт отключён'
    case 'auth/user-not-found':
      return 'Пользователь не найден'
    case 'auth/wrong-password':
      return 'Неверный пароль'
    case 'auth/invalid-credential':
      return 'Неверный email или пароль'
    case 'auth/too-many-requests':
      return 'Слишком много попыток. Попробуйте позже'
    default:
      return error.message || 'Ошибка авторизации'
  }
}
